import React, { useState } from "react";
import { TextField, Button, Typography, Grid, Box, Paper, IconButton, Tooltip } from "@mui/material";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { fieldConfig, getInitialState, labelToNameMap } from "./DefaultParameters";
import { ChevronRight, ChevronLeft } from "@mui/icons-material";

export default function MicroWellParameters({ parameters, setParameters }) {

    const [leftExpanded, setLeftExpanded] = useState(false);

    // Smart parser: "key = value" or "key: value" or tab/comma separated pairs
    const parseSmart = (text) => {
        const lines = text.split("\n");
        const updated = {};
        lines.forEach((line) => {
            if (!line.trim()) return;
            let parts;
            if (line.includes("=")) parts = line.split("=");
            else if (line.includes(":")) parts = line.split(":");
            else if (line.includes("\t")) parts = line.split("\t");
            else if (line.includes(",")) parts = line.split(",");
            else return;
            if (parts.length < 2) return;
            const rawKey = parts[0].trim();
            const value = parts.slice(1).join("").trim();
            const mappedName = labelToNameMap[rawKey.toLowerCase()] || rawKey;
            updated[mappedName] = value;
        });
        return updated;
    };

    const parseByOrder = (text) => {
        const lines = text.split("\n");
        const updated = {};
        fieldConfig.forEach((field, index) => {
            if (lines[index]?.trim()) updated[field.name] = lines[index].trim();
        });
        return updated;
    };

    const handleParse = () => {
        const text = parameters.TXBParse || "";
        const parsed =
            text.includes("=") || text.includes(":") || text.includes("\t") || text.includes(",")
                ? parseSmart(text)
                : parseByOrder(text);
        setParameters(prev => ({ ...prev, ...parsed }));
    };

    const handleReset = () => {
        setParameters(getInitialState());
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setParameters(prev => ({ ...prev, [name]: value }));
    };

    return (
        <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
            {/* Collapsible paste/parse panel */}
            <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                <Tooltip title={leftExpanded ? "Collapse paste panel" : "Expand paste panel"}>
                    <IconButton size="small" onClick={() => setLeftExpanded(v => !v)}>
                        {leftExpanded ? <ChevronLeft /> : <ChevronRight />}
                    </IconButton>
                </Tooltip>
                {leftExpanded && (
                    <Paper elevation={1} sx={{ p: 2, minWidth: 300, mr: 2 }}>
                        <Typography variant="caption" sx={{ display: "block", mb: 1, color: "text.secondary" }}>
                            Paste parameters (key = value, one per line) then click Parse.
                        </Typography>
                        <TextField
                            multiline
                            minRows={6}
                            fullWidth
                            name="TXBParse"
                            value={parameters.TXBParse || ""}
                            onChange={handleChange}
                            placeholder={fieldConfig.map(f => `${f.label} = ${f.default}`).join("\n")}
                            size="small"
                            sx={{ fontFamily: "monospace", mb: 1 }}
                        />
                        <Box sx={{ display: "flex", gap: 1 }}>
                            <Button size="small" variant="outlined" onClick={handleParse}>Parse</Button>
                            <Button size="small" variant="outlined" color="warning" startIcon={<RestartAltIcon />} onClick={handleReset}>Reset</Button>
                        </Box>
                    </Paper>
                )}
            </Box>

            {/* Parameter fields */}
            <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    MicroWell Assay Parameters
                </Typography>
                <Grid container spacing={1.5}>
                    {fieldConfig.map(field => (
                        <Grid item xs={12} sm={6} md={4} key={field.name}>
                            <TextField
                                label={field.label}
                                name={field.name}
                                size="small"
                                fullWidth
                                value={parameters[field.name] ?? field.default}
                                onChange={handleChange}
                                type="number"
                            />
                        </Grid>
                    ))}
                </Grid>

                {/* MGCC curve points */}
                <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                        MGCC Curve Points (optional � up to 10 concentration/signal pairs)
                    </Typography>
                    <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 1 }}>
                        Enter one pair per line: concentration, signal (e.g. "25, 450"). Leave blank to skip MGCC anchoring.
                    </Typography>
                    <TextField
                        multiline
                        minRows={4}
                        fullWidth
                        size="small"
                        placeholder={"25, 450\n50, 870\n100, 1540"}
                        value={(parameters.afMGCCConcentration || []).map((c, i) =>
                            `${c}, ${(parameters.afMGCCSignal || [])[i] ?? ""}`
                        ).join("\n")}
                        onChange={(e) => {
                            const lines = e.target.value.split("\n").filter(l => l.trim());
                            const concs = [], signals = [];
                            lines.slice(0, 10).forEach(line => {
                                const parts = line.split(/[\t,]+/).map(s => s.trim());
                                if (parts.length >= 2) {
                                    const c = Number(parts[0]);
                                    const s = Number(parts[1]);
                                    if (!isNaN(c) && !isNaN(s)) {
                                        concs.push(c);
                                        signals.push(s);
                                    }
                                }
                            });
                            setParameters(prev => ({
                                ...prev,
                                afMGCCConcentration: concs,
                                afMGCCSignal: signals
                            }));
                        }}
                        sx={{ fontFamily: "monospace" }}
                    />
                </Box>
            </Box>
        </Box>
    );
}
